import type { FormEvent } from "react";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormField } from "@/components/ui/form-field";
import PasswordField from "./PasswordField";

type LoginFormProps = {
  email: string;
  password: string;
  rememberMe: boolean;
  loading: boolean;
  error?: string;
  onChangeEmail: (value: string) => void;
  onChangePassword: (value: string) => void;
  onChangeRememberMe: (value: boolean) => void;
  onSubmit: (e: FormEvent) => void;
};

export default function LoginForm({
  email,
  password,
  rememberMe,
  loading,
  error,
  onChangeEmail,
  onChangePassword,
  onChangeRememberMe,
  onSubmit,
}: LoginFormProps) {
  const { t } = useTranslation();

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      {error ? <Alert variant="destructive">{error}</Alert> : null}

      <FormField id="login-email" label={t("auth.email_label")} required>
        <Input
          id="login-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => onChangeEmail(e.target.value)}
          required
          placeholder={t("home.email_placeholder")}
        />
      </FormField>

      <FormField id="login-password" label={t("auth.password_label")} required>
        <PasswordField
          id="login-password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => onChangePassword(e.target.value)}
          required
          placeholder={t("home.password_placeholder")}
        />
      </FormField>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="rememberMe"
          checked={rememberMe}
          onChange={(e) => onChangeRememberMe(e.target.checked)}
          className="h-4 w-4 cursor-pointer rounded border-input text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
        />
        <Label htmlFor="rememberMe" className="cursor-pointer text-muted-foreground">
          {t("home.remember_me")}
        </Label>
      </div>

      <Button
        type="submit"
        variant="gradient"
        size="lg"
        className="w-full"
        loading={loading}
      >
        {loading ? t("home.logging_in") : t("home.login_button")}
      </Button>
    </form>
  );
}
